/**
 * Convert a user-typed game name into a kebab-case identifier that passes
 * the CLI's `validateGameName` regex (`^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$`).
 *
 * - Trim whitespace.
 * - Lowercase EVERY character (catches "II", "MMORPG", "Foo Bar Co.", etc.).
 * - Replace any run of non-`[a-z0-9]` with a single `-`.
 * - Strip leading + trailing dashes.
 *
 * Mirrors `toKebabCase()` in `packages/crucible/src/template/tokens.ts`,
 * inlined here rather than imported because that helper lives in a
 * Node-only package and crucible-ui must remain browser-safe (BG-1 lift).
 *
 * # Why this util exists
 *
 * #1213 regression: Dashboard.tsx Legacy UI's CreateGameDialog passed
 * `gameName.trim()` straight through to the `game:create` IPC. That call
 * routes to `core/create.ts:467`:
 *
 *     const kebab = options.name ?? tokenMap.gameNameKebab.to
 *
 * When `options.name` is set, the CLI's OWN `toKebabCase` slugifier is
 * BYPASSED. The validator on the next line catches invalid slugs but
 * only after the renderer has committed the bad name — the user sees
 * "Invalid game name" with no actionable recovery (the validator's
 * "fix" hint says to change the display name, but the display name
 * field IS the only input + isn't visibly the problem).
 *
 * The fix runs the same lowercasing + replace-runs slugifier in the
 * renderer BEFORE it reaches the IPC. The user's literal display
 * name is preserved separately via `options.displayName`.
 *
 * # Test coverage
 *
 * Adjacent `__tests__/slugify.test.ts` exercises the regression directly
 * (mixed-case, Roman numerals, special chars, leading/trailing/repeated
 * separators, locale gotchas).
 */
export function slugifyToKebab(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
